/**
 * MachineRegistryTab — three sub-tabs (Maintenance / Lift / Equipment) inside
 * the Machine detail modal. Each lists records, lets admins add/edit/delete,
 * shows due-state badges, and has a "Download PDF" button.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Wrench, Anchor, Box, Download, Plus, Trash2, AlertTriangle, Calendar } from "lucide-react";
import { Button, Card, Badge, Spinner, Input, Modal, FileUploadInput } from "../shared/ui";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "maintenance", label: "Maintenance", icon: Wrench, color: "text-blue-600" },
  { id: "lift", label: "Lift Registry", icon: Anchor, color: "text-purple-600" },
  { id: "equipment", label: "Equipment", icon: Box, color: "text-green-600" },
];

const dueBadge = (due) => {
  if (!due) return null;
  if (due.state === "expired") return <Badge variant="danger">OVERDUE ({Math.abs(due.days_until)}d)</Badge>;
  if (due.state === "warning") return <Badge variant="warning">DUE IN {due.days_until}D</Badge>;
  if (due.state === "ok") return <Badge variant="success">{due.days_until}d</Badge>;
  return <Badge>—</Badge>;
};

const MachineRegistryTab = ({ machineId, canEdit }) => {
  const [tab, setTab] = useState("maintenance");
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/machines/${machineId}/registry/${tab}`);
      setRecords(r.data.records);
      setSummary(r.data.summary);
    } catch (e) {
      toast.error("Failed to load registry");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (machineId) load(); /* eslint-disable-next-line */ }, [tab, machineId]);

  const handleAdd = async () => {
    try {
      await axios.post(`${API}/machines/${machineId}/registry/${tab}`, form);
      toast.success("Added");
      setForm({});
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await axios.delete(`${API}/machines/${machineId}/registry/${tab}/${id}`);
    toast.success("Deleted");
    load();
  };

  const downloadPdf = () => {
    window.open(`${API}/machines/${machineId}/registry/${tab}/pdf`, "_blank");
  };

  const isItemType = tab === "lift" || tab === "equipment";

  return (
    <div className="space-y-4" data-testid="machine-registry-tab">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b-2 border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`registry-tab-${t.id}`}
              className={`flex items-center gap-1 px-3 py-2 text-sm font-bold uppercase transition border-b-2 ${
                tab === t.id ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon className={`w-4 h-4 ${tab === t.id ? "text-orange-500" : t.color}`} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Action bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-3 text-sm">
          <span><strong>{summary.total || 0}</strong> total</span>
          {isItemType && (
            <>
              {summary.expired > 0 && <Badge variant="danger">{summary.expired} OVERDUE</Badge>}
              {summary.warning > 0 && <Badge variant="warning">{summary.warning} DUE SOON</Badge>}
            </>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/machines/${machineId}/usage-log/pdf`, "_blank")} data-testid="machine-usage-log-pdf">
            <Download className="w-4 h-4 mr-1" /> Usage Log
          </Button>
          <Button variant="outline" size="sm" onClick={downloadPdf} data-testid={`registry-pdf-${tab}`}>
            <Download className="w-4 h-4 mr-1" /> PDF
          </Button>
          {canEdit && (
            <Button size="sm" onClick={() => setShowAdd(true)} data-testid={`registry-add-${tab}`}>
              <Plus className="w-4 h-4 mr-1" /> Add Record
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : records.length === 0 ? (
        <p className="text-center text-slate-500 py-6">No records yet. Click <strong>Add Record</strong> to start.</p>
      ) : (
        <div className="space-y-2">
          {records.map((r) => (
            <Card key={r.id} className="p-3" data-testid={`registry-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {isItemType ? (
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold">{r.item_name || "Unnamed"}</p>
                        {r.serial_number && <span className="text-xs text-slate-500">#{r.serial_number}</span>}
                        {dueBadge(r.due)}
                      </div>
                      <p className="text-xs text-slate-600 mt-1">
                        Last test: {r.last_test_date || "—"} · Next due: <strong>{r.next_due_date}</strong>
                      </p>
                      {r.notes && <p className="text-xs text-slate-500 italic mt-1">{r.notes}</p>}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold">{r.description || "Maintenance"}</p>
                        {r.cost != null && <Badge>${Number(r.cost).toLocaleString()}</Badge>}
                      </div>
                      <p className="text-xs text-slate-600 mt-1">
                        {r.performed_at_date || "—"} by {r.performed_by || "—"}
                      </p>
                      {r.next_due_date && <p className="text-xs text-orange-600 mt-1"><Calendar className="w-3 h-3 inline mr-1" />Next service: {r.next_due_date}</p>}
                      {(r.receipt_files && r.receipt_files.length > 0) && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {r.receipt_files.map((f, i) => (
                            <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" className="text-[10px] bg-slate-100 hover:bg-slate-200 px-2 py-0.5 rounded text-blue-600">
                              {f.name || `Receipt ${i + 1}`}
                            </a>
                          ))}
                        </div>
                      )}
                      {r.notes && <p className="text-xs text-slate-500 italic mt-1">{r.notes}</p>}
                    </>
                  )}
                </div>
                {canEdit && (
                  <button onClick={() => handleDelete(r.id)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`registry-del-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title={`Add ${tab.charAt(0).toUpperCase() + tab.slice(1)} Record`}>
          <div className="space-y-3">
            {isItemType ? (
              <>
                <Input label="Item Name" value={form.item_name || ""} onChange={(e) => setForm({ ...form, item_name: e.target.value })} />
                <Input label="Serial Number" value={form.serial_number || ""} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} />
                <Input label="Last Test Date" type="date" value={form.last_test_date || ""} onChange={(e) => setForm({ ...form, last_test_date: e.target.value })} />
                <p className="text-xs text-slate-500">Next due will auto-calculate at +3 months.</p>
                <Input label="Notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </>
            ) : (
              <>
                <Input label="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. 250hr service, brake repair..." />
                <Input label="Date" type="date" value={form.performed_at_date || ""} onChange={(e) => setForm({ ...form, performed_at_date: e.target.value })} />
                <Input label="Performed By" value={form.performed_by || ""} onChange={(e) => setForm({ ...form, performed_by: e.target.value })} placeholder="Mechanic / company" />
                <Input label="Cost ($)" type="number" value={form.cost || ""} onChange={(e) => setForm({ ...form, cost: parseFloat(e.target.value) || null })} />
                <Input label="Next Service Due (optional)" type="date" value={form.next_due_date || ""} onChange={(e) => setForm({ ...form, next_due_date: e.target.value })} />
                <div>
                  <label className="text-sm font-bold uppercase text-slate-600 block mb-1">Receipts / Photos</label>
                  <FileUploadInput
                    accept="image/*,application/pdf"
                    onFileSelect={(url, name) => setForm({ ...form, receipt_files: [...(form.receipt_files || []), { url, name }] })}
                    currentFile={null}
                  />
                  {(form.receipt_files || []).length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {form.receipt_files.map((f, i) => (
                        <li key={i} className="flex items-center justify-between text-xs bg-slate-50 px-2 py-1 rounded">
                          <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">{f.name}</a>
                          <button type="button" onClick={() => setForm({ ...form, receipt_files: form.receipt_files.filter((_, idx) => idx !== i) })} className="text-rose-500">&times;</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Input label="Notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </>
            )}
            <div className="flex gap-3 pt-2">
              <Button onClick={handleAdd} data-testid="registry-save-btn">Save</Button>
              <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MachineRegistryTab;
